import { createAsyncThunk } from "@reduxjs/toolkit";
import realAuthService from "../../../helpers/real_auth_helper";
import TokenManagerHelper from "../../../helpers/token_manager_helper";
import { persistor } from "../../../store";
import { setCustomers } from "../../customers/reducer";
import { resetDashboardState } from "../../dashboard/reducer";
import { setPaymentForms } from "../../paymentForms/reducer";
import { setPaymentMethods } from "../../paymentMethods/reducer";
import { setProducts } from "../../products/reducer";
import { loginError, loginStart, loginSuccess, logoutSuccess } from "./reducer";

interface LoginCredentials {
  proprietario: string;
  cpfCnpj: string;
  password: string;
}

export const loginAndFetchData = createAsyncThunk<
  { success: boolean },
  LoginCredentials,
  { rejectValue: string }
>("auth/loginAndFetchData", async (credentials, { dispatch, rejectWithValue }) => {
  try {
    dispatch(loginStart());

    dispatch(resetDashboardState());

    const responseData = await realAuthService.login(credentials);

    if (!responseData.token) {
      throw new Error("Token não recebido do servidor");
    }

    let userData;
    if (responseData.user) {
      const userId = responseData.user.uuid || responseData.user.id || responseData.user.uid;
      if (!userId) {
        console.error("❌ responseData.user:", responseData.user);
        throw new Error("ID do usuário não recebido do servidor (tentou: uuid, id, uid)");
      }

      userData = {
        documentOwner: credentials.proprietario,
        id: Number(userId),
        name: responseData.user.name || responseData.user.username || credentials.cpfCnpj,
        user: responseData.user.username || credentials.cpfCnpj,
        password: "",
        lastSyncDate: new Date().toISOString(),
      };
    } else {
      let jwtUsername = null;
      try {
        const payload = responseData.token.replace("Bearer ", "").split(".")[1];
        const decoded = JSON.parse(atob(payload));
        jwtUsername = decoded.username;
      } catch {
        // Falha silenciosa na decodificação do JWT
      }

      userData = {
        documentOwner: credentials.proprietario,
        id: Date.now(),
        name: jwtUsername || credentials.cpfCnpj,
        user: jwtUsername || credentials.cpfCnpj,
        password: "",
        lastSyncDate: new Date().toISOString(),
      };
    }

    TokenManagerHelper.setAuthToken(responseData.token);

    dispatch(
      loginSuccess({
        user: userData,
        token: responseData.token,
      })
    );
    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Erro desconhecido no login";
    console.error("❌ Erro no thunk:", errorMessage);
    console.error("❌ Stack trace completo:", error instanceof Error ? error.stack : error);
    console.error("❌ Tipo do erro:", typeof error);
    console.error("❌ Erro original:", error);
    dispatch(loginError(errorMessage));
    return rejectWithValue(errorMessage);
  }
});

export const logoutUser = createAsyncThunk("auth/logout", async (_, { dispatch }) => {
  TokenManagerHelper.clearAuthToken();

  localStorage.removeItem("authUser");
  localStorage.removeItem("accessToken");

  dispatch(setCustomers([]));
  dispatch(setPaymentMethods([]));
  dispatch(setPaymentForms([]));
  dispatch(setProducts([]));

  dispatch(logoutSuccess());

  await persistor.purge();

  return true;
});
